import type { Action, ThunkDispatch } from '@reduxjs/toolkit';

import type { AssignedTablesType } from '../../../api/fetchers/assignedTables/types';
import type {
  PrizeDropWinSocketResponse,
  Promotion,
  RoundHistoryByWSType,
  RoundHistoryType,
  WinNotificationType,
} from '../../../api/fetchers/promotionsV2/types/promotions';
import type { PromotionPointsJSON } from '../../../api/fetchers/promotionsV2/types/promotionsApi';

/*
  PromotionsV2 slice will be used in a standalone Promotions component with its own
  own redux store.
    - It needs to hold required user details, information to make api calls/refresh jwt.
      `PromosHydrationData` represents this data and is loaded into the slice.
    - Use `PromosCoreState` and `PromosCoreDispatch` instead of `CoreRootState` and 
      `CoreDispatch` to type State and Dispatch inside the new promotions component.
*/
export type PromosCoreState<S = object> = S & { promotionsV2: PromotionsV2State };

export type PromosCoreDispatch<S = object> = ThunkDispatch<PromosCoreState<S>, unknown, Action>;

export type PromosHydrationData = Omit<PromotionsV2State, keyof PromosData>;

export type PromotionsV2State = PromosData &
  Partial<PromosUserData> &
  Partial<PromosGameData> &
  Partial<PromosApiAuthData> &
  Partial<PromosJwtAuthData> &
  Partial<PrizeDropWinMessageData> &
  Partial<PromosFeatureConfig>;

export interface PrizeDropWinMessageData {
  prizeDropWinMessage: PrizeDropWinSocketResponse;
}

export interface PromosData {
  promotions: Array<Promotion>; // promotions
  promosPopup: PromosPopup | null; // to show active popup
  showPromoEar: boolean; // to show ear
  isOptPromosLoading: boolean;
  topTournament?: TopTournamentPoints; // win Streak
  points: Array<PromoPoint>;
  selectedPromoId: number | null;
  assignedTables: AssignedTablesType | null;
  isNewPromoAvailable: boolean;
  winNotifications: Array<WinNotificationType>;
  roundHistory: Array<RoundHistoryType>;
  roundHistoryByWs: Array<RoundHistoryByWSType>;
  isRoundHistoryLoading: boolean;
  showDetailsModal: boolean;
  showScorePopup: boolean;
  isPromotionsInitialised: boolean;
}

export interface PromosUserData {
  userId: string;
  lang: string;
  screenName: string;
  casinoRegion: string;
}

export interface PromosGameData {
  gameId: string;
  currency: string;
  isBetOpenStatus: boolean;
  totalBet: number;
  isResponsive?: boolean;
}
export interface PromosFeatureConfig {
  featureConfig?: PromoFeatures;
}

export interface PromoFeatures {
  [key: string]: boolean;
}
export interface PromosApiAuthData {
  envPromo: string;
  jwtToken: string;
}

export interface PromosJwtAuthData {
  envGs: string;
  jsessionId: string;
}

export enum PromosPopup {
  PromosConsent = 'PromosConsent',
  PromosDetails = 'PromosDetails',
  PromosWinNotification = 'PromosWinNotification',
}

export interface PromoPoint extends PromotionPointsJSON {
  id: number;
  toEpoch: number;
  minimumBetLimit: number;
}
export interface TopTournamentPoints extends PromoPoint {}

export interface Trophy {
  enable: boolean;
  isActive?: boolean;
  toolTipContent?: string;
  minBet?: string;
}
